// Speaking a note instead of typing it (playbook review, 0.5). Uses the browser's own speech recognition where it
// exists (Chrome, Edge, Safari); elsewhere the button is not shown. Nothing is recorded or uploaded by the app: the
// browser turns speech into text and only the text is saved, as a note the person can read and edit first.
import { useCallback, useEffect, useRef, useState } from "react";

interface RecognitionResult {
  isFinal: boolean;
  0: { transcript: string };
}
interface RecognitionEvent {
  resultIndex: number;
  results: ArrayLike<RecognitionResult>;
}
interface Recognition {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((e: RecognitionEvent) => void) | null;
  onerror: ((e: { error?: string }) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
}
type RecognitionCtor = new () => Recognition;

function ctor(): RecognitionCtor | null {
  if (typeof window === "undefined") return null;
  const w = window as unknown as { SpeechRecognition?: RecognitionCtor; webkitSpeechRecognition?: RecognitionCtor };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export function speechSupported(): boolean {
  return ctor() !== null;
}

/**
 * Dictation into a text box. `onText` gets each finished phrase; `interim` is what is being heard now.
 * `error` is a sentence for the person (for example when the microphone is blocked).
 */
export function useDictation(onText: (text: string) => void, lang = "en-GB") {
  const [listening, setListening] = useState(false);
  const [interim, setInterim] = useState("");
  const [error, setError] = useState<string | null>(null);
  const rec = useRef<Recognition | null>(null);
  const cb = useRef(onText);
  cb.current = onText;

  const stop = useCallback(() => {
    rec.current?.stop();
  }, []);

  const start = useCallback(() => {
    const C = ctor();
    if (!C) {
      setError("Speaking a note isn't available in this browser. Type it instead.");
      return;
    }
    setError(null);
    const r = new C();
    r.lang = lang;
    r.continuous = true;
    r.interimResults = true;
    r.onresult = (e) => {
      let heard = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const res = e.results[i]!;
        const t = res[0].transcript;
        if (res.isFinal) cb.current(t.trim());
        else heard += t;
      }
      setInterim(heard.trim());
    };
    r.onerror = (e) => {
      setError(
        e.error === "not-allowed" || e.error === "service-not-allowed"
          ? "The microphone is blocked for this site. Allow it in the browser's address bar, or type the note."
          : e.error === "no-speech"
            ? "We didn't hear anything. Try again, a little closer to the microphone."
            : "Speech stopped working. Type the note instead.",
      );
    };
    r.onend = () => {
      setListening(false);
      setInterim("");
      rec.current = null;
    };
    rec.current = r;
    try {
      r.start();
      setListening(true);
    } catch {
      setError("Speech couldn't start. Type the note instead.");
    }
  }, [lang]);

  useEffect(() => () => rec.current?.stop(), []);

  return { supported: speechSupported(), listening, interim, error, start, stop };
}
